import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../utils/currency';
import { getApiUrl } from '../config';
import './Charts.css';

const Charts = ({ config }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pieChartLoading, setPieChartLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState({
    monthlyIncome: [],
    monthlyExpenses: [],
    expensesByCategory: [],
    expensesByPayee: [],
    netWorthTrend: []
  });
  const [drillDownPath, setDrillDownPath] = useState([]); // Track current drill-down path
  const [timePeriod, setTimePeriod] = useState('thisyear'); // Configurable time period
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'include', 'exclude'
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);

  const COLORS = ['#58a6ff', '#3fb950', '#d29922', '#f85149', '#79c0ff', '#bc8cff', '#ffa657'];

  // Convert time period to hledger-compatible format
  const getHledgerPeriod = (period) => {
    // Show all time - no period filter
    if (period === 'all') {
      return '';
    }

    // For "lastNdays", calculate date range instead
    if (period === 'last30days' || period === 'last90days' || period === 'last365days') {
      const today = new Date();
      const daysAgo = period === 'last30days' ? 30 : period === 'last90days' ? 90 : 365;
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - daysAgo);

      const start = startDate.toISOString().split('T')[0];
      const end = today.toISOString().split('T')[0];
      return `${start}..${end}`;
    }

    // Standard hledger periods
    return period;
  };

  // Custom tooltip formatter for currency
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: '6px',
          padding: '8px 12px'
        }}>
          <p style={{ margin: '0 0 4px 0', color: '#8b949e' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '2px 0', color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Y-axis formatter for currency
  const formatYAxis = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  // Load available expense accounts for filtering
  const loadAvailableAccounts = useCallback(async () => {
    try {
      const response = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: 'accounts expenses --depth 2'
      });

      const accounts = response.data.stdout
        .split('\n')
        .filter(a => a.trim())
        .map(a => a.trim());

      setAvailableAccounts(accounts);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  }, [config]);

  // Build account filter for hledger queries
  const buildAccountFilter = () => {
    if (filterMode === 'all' || selectedAccounts.length === 0) {
      return '';
    }

    if (filterMode === 'include') {
      // Include only selected accounts - just list them
      return selectedAccounts.join(' ');
    } else {
      // Exclude selected accounts - use not: prefix
      return selectedAccounts.map(acc => `not:${acc}`).join(' ');
    }
  };

  const loadChartData = useCallback(async (path = []) => {
    setLoading(true);
    setError('');

    try {
      // Use selected time period
      const hledgerPeriod = getHledgerPeriod(timePeriod);
      const periodFlag = hledgerPeriod ? `-p ${hledgerPeriod}` : '';

      // Get monthly expenses for selected period
      const expensesResponse = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: `balance expenses -M --output-format=json ${periodFlag}`.trim()
      });

      // Get monthly income for selected period
      const incomeResponse = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: `balance income -M --output-format=json ${periodFlag}`.trim()
      });

      // Build account filter
      const accountFilter = buildAccountFilter();

      // Build category query based on drill-down path
      let categoryQuery;
      if (path.length === 0) {
        // Top level: show main expense categories (depth 2)
        if (filterMode === 'all' || selectedAccounts.length === 0) {
          categoryQuery = `balance expenses --depth 2 --output-format=json ${periodFlag}`.trim();
        } else if (filterMode === 'include') {
          // Include: query specific accounts
          const accounts = selectedAccounts.join(' ');
          categoryQuery = `balance ${accounts} --depth 2 --output-format=json ${periodFlag}`.trim();
        } else {
          // Exclude: expenses with not: filters
          categoryQuery = `balance expenses ${accountFilter} --depth 2 --output-format=json ${periodFlag}`.trim();
        }
      } else {
        // Drilled down: show subcategories of the selected category
        const category = path[path.length - 1];
        // Quote the category if it contains spaces
        const quotedCategory = category.includes(' ') ? `"${category}"` : category;

        if (filterMode === 'all' || selectedAccounts.length === 0) {
          categoryQuery = `balance ${quotedCategory} --depth ${path.length + 2} --output-format=json ${periodFlag}`.trim();
        } else if (filterMode === 'include') {
          // Include: query specific subcategories
          const accounts = selectedAccounts.map(acc => `${category}:${acc.split(':').pop()}`).join(' ');
          categoryQuery = `balance ${accounts} --depth ${path.length + 2} --output-format=json ${periodFlag}`.trim();
        } else {
          // Exclude: category with not: filters
          categoryQuery = `balance ${quotedCategory} ${accountFilter} --depth ${path.length + 2} --output-format=json ${periodFlag}`.trim();
        }
      }

      // Get expenses by category
      const categoryResponse = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: categoryQuery
      });

      // Get expenses by payee using register command
      const payeeResponse = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: `register expenses --output-format=json ${periodFlag}`.trim()
      });

      console.log('Payee command:', `register expenses --output-format=json ${periodFlag}`.trim());
      console.log('Payee response:', payeeResponse.data);
      console.log('Payee stdout length:', payeeResponse.data.stdout?.length);

      // Parse and format data
      const expensesData = parseMonthlyData(expensesResponse.data.stdout);
      const incomeData = parseMonthlyData(incomeResponse.data.stdout);
      const categoryData = parseCategoryData(categoryResponse.data.stdout);
      const payeeData = parsePayeeData(payeeResponse.data.stdout);

      console.log('Parsed payee data:', payeeData);

      // Check if we've hit bottom (no subcategories) during initial load
      if (path.length > 0) {
        const lastPath = path[path.length - 1];

        // Check if we're at a leaf node (same as in loadPieChartData)
        const isLeafNode = categoryData.length === 0 ||
                           (categoryData.length === 1 && categoryData[0].fullName === lastPath) ||
                           categoryData.every(item => item.fullName === lastPath);

        if (isLeafNode) {
          // Hit bottom - revert the drill-down
          setDrillDownPath(path.slice(0, -1));
          setLoading(false);
          return;
        }
      }

      // Merge income and expenses by month for the line chart
      const monthlyTrend = mergeMonthlyData(incomeData, expensesData);

      setChartData({
        monthlyExpenses: expensesData,
        monthlyIncome: monthlyTrend,
        expensesByCategory: categoryData,
        expensesByPayee: payeeData
      });

      setLoading(false);
    } catch (err) {
      setError('Error loading chart data: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  }, [config, timePeriod]);

  const loadPieChartData = useCallback(async (path = []) => {
    setPieChartLoading(true);

    try {
      // Build account filter
      const accountFilter = buildAccountFilter();
      const hledgerPeriod = getHledgerPeriod(timePeriod);
      const periodFlag = hledgerPeriod ? `-p ${hledgerPeriod}` : '';

      // Build category query based on drill-down path
      let categoryQuery;
      if (path.length === 0) {
        // Top level: show main expense categories (depth 2)
        if (filterMode === 'all' || selectedAccounts.length === 0) {
          categoryQuery = `balance expenses --depth 2 --output-format=json ${periodFlag}`.trim();
        } else if (filterMode === 'include') {
          // Include: query specific accounts
          const accounts = selectedAccounts.join(' ');
          categoryQuery = `balance ${accounts} --depth 2 --output-format=json ${periodFlag}`.trim();
        } else {
          // Exclude: expenses with not: filters
          categoryQuery = `balance expenses ${accountFilter} --depth 2 --output-format=json ${periodFlag}`.trim();
        }
      } else {
        // Drilled down: show subcategories of the selected category
        const category = path[path.length - 1];
        // Quote the category if it contains spaces
        const quotedCategory = category.includes(' ') ? `"${category}"` : category;

        if (filterMode === 'all' || selectedAccounts.length === 0) {
          categoryQuery = `balance ${quotedCategory} --depth ${path.length + 2} --output-format=json ${periodFlag}`.trim();
        } else if (filterMode === 'include') {
          // Include: query specific subcategories
          const accounts = selectedAccounts.map(acc => `${category}:${acc.split(':').pop()}`).join(' ');
          categoryQuery = `balance ${accounts} --depth ${path.length + 2} --output-format=json ${periodFlag}`.trim();
        } else {
          // Exclude: category with not: filters
          categoryQuery = `balance ${quotedCategory} ${accountFilter} --depth ${path.length + 2} --output-format=json ${periodFlag}`.trim();
        }
      }

      // Get expenses by category
      const categoryResponse = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: categoryQuery
      });

      const newData = parseCategoryData(categoryResponse.data.stdout);

      // Check if we've hit bottom (no subcategories)
      // If we drilled down but got the same category back, we're at a leaf node
      if (path.length > 0) {
        const lastPath = path[path.length - 1];

        // Check if we're at a leaf node:
        // 1. No data returned
        // 2. Only got 1 item back and it's the same as what we clicked
        // 3. All returned items have the exact same fullName as what we clicked
        const isLeafNode = newData.length === 0 ||
                           (newData.length === 1 && newData[0].fullName === lastPath) ||
                           newData.every(item => item.fullName === lastPath);

        console.log('Leaf check:', { lastPath, newDataLength: newData.length, newData, isLeafNode });

        if (isLeafNode) {
          // Hit bottom - revert the drill-down
          console.log('Leaf node detected, reverting');
          setDrillDownPath(path.slice(0, -1));
          setPieChartLoading(false);
          return;
        }
      }

      setChartData(prevData => ({
        ...prevData,
        expensesByCategory: newData
      }));

      setPieChartLoading(false);
    } catch (err) {
      setError('Error loading pie chart data: ' + (err.response?.data?.error || err.message));
      setPieChartLoading(false);
    }
  }, [config, timePeriod, filterMode, selectedAccounts]);

  useEffect(() => {
    loadAvailableAccounts();
  }, [loadAvailableAccounts]);

  useEffect(() => {
    setDrillDownPath([]); // Reset drill-down when config or time period changes
    loadChartData([]);
  }, [config, timePeriod, loadChartData]);

  useEffect(() => {
    if (!loading) { // Only reload pie chart if initial load is complete
      loadPieChartData(drillDownPath);
    }
  }, [drillDownPath, loading, loadPieChartData]);

  // Reload pie chart when filters change (without full page reload)
  useEffect(() => {
    if (!loading) {
      loadPieChartData(drillDownPath);
    }
  }, [filterMode, selectedAccounts]);

  const parseMonthlyData = (jsonStr) => {
    try {
      const data = JSON.parse(jsonStr);

      // hledger periodic report structure: prDates, prTotals
      if (!data.prDates || !data.prTotals) {
        console.error('Invalid periodic report structure');
        return [];
      }

      return data.prDates.map((dateRange, index) => {
        // Get month from first date in range: "2024-01-01" -> "2024-01"
        const month = dateRange[0].contents.substring(0, 7);

        // Get amount for this period from prTotals.prrAmounts[index]
        const amounts = data.prTotals.prrAmounts[index];
        const total = amounts.length > 0 && amounts[0].aquantity
          ? Math.abs(amounts[0].aquantity.floatingPoint)
          : 0;

        return { month, amount: total };
      }).filter(item => item.month); // Filter out any invalid entries
    } catch (err) {
      console.error('Error parsing monthly data:', err);
      return [];
    }
  };

  const parseCategoryData = (jsonStr) => {
    try {
      const data = JSON.parse(jsonStr);

      // Balance output structure: [accountsArray, totalsArray]
      if (!Array.isArray(data) || data.length === 0) {
        console.error('Invalid balance structure');
        return [];
      }

      const accounts = data[0]; // Extract accounts array
      if (!Array.isArray(accounts)) {
        return [];
      }

      // Each account: [shortName, fullName, depth, [amounts]]
      return accounts
        .map(account => {
          const shortName = account[0];
          const fullName = account[1];
          const amounts = account[3];

          if (!amounts || !Array.isArray(amounts) || amounts.length === 0) {
            return null;
          }

          // Get the amount value
          const amount = amounts[0]?.aquantity?.floatingPoint || 0;

          return {
            name: typeof shortName === 'string' ? shortName : String(shortName),
            fullName: typeof fullName === 'string' ? fullName : String(fullName),
            value: Math.abs(amount)
          };
        })
        .filter(item => item && item.value > 0) // Filter out nulls and zero values
        .sort((a, b) => b.value - a.value) // Sort by value descending
        .slice(0, 10); // Top 10 categories
    } catch (err) {
      console.error('Error parsing category data:', err);
      return [];
    }
  };

  const parsePayeeData = (jsonStr) => {
    try {
      const data = JSON.parse(jsonStr);

      // Register output structure: array of arrays [date, code, description, metadata, postings]
      if (!Array.isArray(data)) {
        console.error('Invalid register structure - not an array');
        return [];
      }

      if (data.length === 0) {
        return [];
      }

      // Aggregate amounts by payee
      const payeeMap = new Map();

      data.forEach((txn) => {
        // Transaction is an array: [0]=date, [1]=code, [2]=description/payee, [3]=metadata, [4]=postings
        const payee = txn[2] || 'Unknown';
        const postings = txn[4] || [];

        // Sum up expense postings for this transaction
        if (Array.isArray(postings)) {
          postings.forEach((posting) => {
            // Posting is also an array: [0]=account, [1]=amount, [2]=balance, [3]=comment
            const account = posting[0] || '';
            const amountData = posting[1] || [];

            // Only count expense accounts
            if (account.startsWith('expenses:')) {
              // Amount data is an array of amount objects
              if (Array.isArray(amountData) && amountData.length > 0) {
                const amount = amountData[0]?.aquantity
                  ? parseFloat(amountData[0].aquantity)
                  : 0;

                if (amount !== 0) {
                  const current = payeeMap.get(payee) || 0;
                  payeeMap.set(payee, current + Math.abs(amount));
                }
              }
            }
          });
        }
      });

      // Convert to array and sort
      return Array.from(payeeMap.entries())
        .map(([payee, amount]) => ({ payee, amount }))
        .filter(item => item.amount > 0)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 15); // Top 15 payees
    } catch (err) {
      console.error('Error parsing payee data:', err);
      return [];
    }
  };

  const mergeMonthlyData = (incomeData, expensesData) => {
    // Create a map of all unique months
    const monthMap = new Map();

    // Add income data
    incomeData.forEach(item => {
      monthMap.set(item.month, { month: item.month, income: item.amount, expenses: 0 });
    });

    // Add/update with expenses data
    expensesData.forEach(item => {
      if (monthMap.has(item.month)) {
        monthMap.get(item.month).expenses = item.amount;
      } else {
        monthMap.set(item.month, { month: item.month, income: 0, expenses: item.amount });
      }
    });

    // Convert to array and sort by month
    return Array.from(monthMap.values()).sort((a, b) => a.month.localeCompare(b.month));
  };

  // Handle pie slice click to drill down
  const handlePieClick = (data) => {
    if (data && data.fullName) {
      setDrillDownPath([...drillDownPath, data.fullName]);
    }
  };

  // Handle label click to view transactions
  const handleLabelClick = (data, event) => {
    if (event) {
      event.stopPropagation(); // Prevent pie slice click
    }
    if (data && data.fullName) {
      // Navigate to transactions page with filter
      navigate(`/transactions?account=${encodeURIComponent(data.fullName)}&period=${timePeriod}`);
    }
  };

  // Handle back button to go up one level
  const handleBack = () => {
    if (drillDownPath.length > 0) {
      setDrillDownPath(drillDownPath.slice(0, -1));
    }
  };

  // Custom label with click handler
  const renderCustomLabel = (entry) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, outerRadius, name } = entry;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#c9d1d9"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ cursor: 'pointer', fontSize: '14px' }}
        onClick={(e) => handleLabelClick(entry, e)}
      >
        {name}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading charts...</p>
      </div>
    );
  }

  return (
    <div className="charts-page">
      <div className="page-header">
        <h1>Charts & Visualizations</h1>
        <p>Visual analysis of your financial data</p>
      </div>

      {/* Time Period and Account Filter Selector */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Time Period Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#c9d1d9', minWidth: '120px' }}>Time Period:</label>
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '6px',
                  color: '#c9d1d9',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Time</option>
                <option value="thismonth">This Month</option>
                <option value="thisquarter">This Quarter</option>
                <option value="thisyear">This Year</option>
                <option value="lastmonth">Last Month</option>
                <option value="lastquarter">Last Quarter</option>
                <option value="lastyear">Last Year</option>
                <option value="last30days">Last 30 Days</option>
                <option value="last90days">Last 90 Days</option>
                <option value="last365days">Last 365 Days</option>
              </select>
            </div>

            {/* Account Filter */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#c9d1d9', minWidth: '120px', paddingTop: '8px' }}>Account Filter:</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      setFilterMode('all');
                      setSelectedAccounts([]);
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: filterMode === 'all' ? '#1f6feb' : '#21262d',
                      border: '1px solid #30363d',
                      borderRadius: '6px',
                      color: '#c9d1d9',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilterMode('include')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: filterMode === 'include' ? '#1f6feb' : '#21262d',
                      border: '1px solid #30363d',
                      borderRadius: '6px',
                      color: '#c9d1d9',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Include
                  </button>
                  <button
                    onClick={() => setFilterMode('exclude')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: filterMode === 'exclude' ? '#1f6feb' : '#21262d',
                      border: '1px solid #30363d',
                      borderRadius: '6px',
                      color: '#c9d1d9',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Exclude
                  </button>
                  {filterMode !== 'all' && selectedAccounts.length > 0 && (
                    <span style={{ fontSize: '0.85em', color: '#8b949e', marginLeft: '8px' }}>
                      ({selectedAccounts.length} selected)
                    </span>
                  )}
                </div>

                {filterMode !== 'all' && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#0d1117',
                    border: '1px solid #30363d',
                    borderRadius: '6px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {availableAccounts.map(account => (
                      <label
                        key={account}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: selectedAccounts.includes(account) ? '#161b22' : 'transparent',
                          border: selectedAccounts.includes(account) ? '1px solid #30363d' : '1px solid transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAccounts([...selectedAccounts, account]);
                            } else {
                              setSelectedAccounts(selectedAccounts.filter(a => a !== account));
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '13px', color: '#c9d1d9' }}>
                          {account.replace('expenses:', '')}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Income vs Expenses</h2>
        </div>
        <div className="card-body">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.monthlyIncome}>
                <CartesianGrid strokeDasharray="3 3" stroke="#30363d" />
                <XAxis dataKey="month" stroke="#8b949e" />
                <YAxis stroke="#8b949e" tickFormatter={formatYAxis} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="#3fb950" strokeWidth={2} animationDuration={300} />
                <Line type="monotone" dataKey="expenses" stroke="#f85149" strokeWidth={2} animationDuration={300} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {chartData.monthlyIncome.length === 0 && (
            <div className="empty-state">
              <p>No data available for the selected period</p>
            </div>
          )}
        </div>
      </div>

      {/* Interactive Expenses Pie Chart - Full Width */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <h2 className="card-title">Expenses by Category</h2>
            {drillDownPath.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9em' }}>
                <span
                  onClick={() => setDrillDownPath([])}
                  style={{
                    color: '#58a6ff',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  All Expenses
                </span>
                {drillDownPath.map((path, index) => (
                  <React.Fragment key={index}>
                    <span style={{ color: '#8b949e' }}>→</span>
                    <span
                      onClick={() => setDrillDownPath(drillDownPath.slice(0, index + 1))}
                      style={{
                        color: index === drillDownPath.length - 1 ? '#c9d1d9' : '#58a6ff',
                        cursor: index === drillDownPath.length - 1 ? 'default' : 'pointer',
                        textDecoration: 'none',
                        fontWeight: index === drillDownPath.length - 1 ? '500' : 'normal'
                      }}
                      onMouseEnter={(e) => {
                        if (index !== drillDownPath.length - 1) {
                          e.target.style.textDecoration = 'underline';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      {path.split(':').pop()}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '0.9em', color: '#8b949e' }}>
            Click slices to drill deeper • Click labels to view transactions
          </p>
        </div>
        <div className="card-body" style={{ position: 'relative' }}>
          {pieChartLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            }}>
              <div className="spinner"></div>
            </div>
          )}
          <div className="chart-container" style={{ opacity: pieChartLoading ? 0.5 : 1 }}>
            <ResponsiveContainer width="100%" height={600}>
              <PieChart>
                <Pie
                  data={chartData.expensesByCategory}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={renderCustomLabel}
                  outerRadius={200}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={handlePieClick}
                  cursor="pointer"
                  animationDuration={300}
                >
                  {chartData.expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {chartData.expensesByCategory.length === 0 && !pieChartLoading && (
            <div className="empty-state">
              <p>No expense data available for this category</p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Expense Trend</h2>
        </div>
        <div className="card-body">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.monthlyExpenses}>
                <CartesianGrid strokeDasharray="3 3" stroke="#30363d" />
                <XAxis dataKey="month" stroke="#8b949e" />
                <YAxis stroke="#8b949e" tickFormatter={formatYAxis} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" fill="#58a6ff" animationDuration={300} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {chartData.monthlyExpenses.length === 0 && (
            <div className="empty-state">
              <p>No expense trend data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Expenses by Payee */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Top Expenses by Payee</h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '0.9em', color: '#8b949e' }}>
            Top 15 payees by total expense amount
          </p>
        </div>
        <div className="card-body">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData.expensesByPayee} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#30363d" />
                <XAxis type="number" stroke="#8b949e" tickFormatter={formatYAxis} />
                <YAxis dataKey="payee" type="category" stroke="#8b949e" width={150} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" fill="#d29922" animationDuration={300} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {chartData.expensesByPayee.length === 0 && (
            <div className="empty-state">
              <p>No payee data available</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Charts;
